import * as Yup from 'yup';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import {
    PoolButton,
    PoolFormikTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
} from '@synergy/components-next';
import { PoolMarkdownEditor } from '@synergy/components';

import { InvoiceGenericItem, InvoiceGenericItemType } from '../../../../models/invoice-generic-item';
import { InvoiceSteps } from '../../../models/invoice-steps';
import { ItemsArrayHelper } from './detail-positions-table-container';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { getHighestId } from '../utils/get-highest-id';
import { getLastPosition } from '../utils/get-last-position';
import { invoiceDetailStepperStateSelector } from '../../../store/invoice-detail-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

interface GenericItemModalProps {
    showGenericItemModal: boolean;
    onModalClose: () => void;
    helper: ItemsArrayHelper;
    genericItemIndex?: number;
    genericItem?: InvoiceGenericItem;
    genericItemType?: InvoiceGenericItemType;
    taxRates: TaxRate[];
}

export const GenericItemModal = (props: GenericItemModalProps) => {
    const { showGenericItemModal, onModalClose, helper, genericItemIndex, genericItem, taxRates, genericItemType } =
        props;
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Positions);
    const intl = useIntl();

    const handleSubmit = (values: Partial<InvoiceGenericItem>) => {
        if (genericItem && genericItemIndex !== undefined) {
            helper.replace(genericItemIndex, values);
        } else {
            helper.push(values);
        }
        onModalClose();
    };

    const handleTaxRateSelected = (formik: FormikProps<Partial<InvoiceGenericItem>>, value: TaxRate | null): void => {
        formik.setFieldValue(`tax`, value?.taxPercentage ?? null);
    };

    const handleClose = (formik: FormikProps<Partial<InvoiceGenericItem>>) => () => {
        formik.resetForm();
        onModalClose();
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required('app.error.required'),
        taxRate: Yup.object().nullable().required('app.error.required'),
    });

    const initialValues: Partial<InvoiceGenericItem> = genericItem || {
        id: getHighestId(helper) + 1,
        position: getLastPosition(helper) + 1,
        taxRate: undefined,
        title: intl.formatMessage({ id: 'app.invoices.detail.genericItemDefaultTitle.' + genericItemType }),
        type: genericItemType,
        tax: undefined,
        quantity: 1,
        price: 0,
        discount: 0,
    };

    return (
        <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
        >
            {formikProps => (
                <PoolModal open={showGenericItemModal} onClose={handleClose(formikProps)}>
                    <PoolModalTitle>
                        <FormattedMessage
                            id={
                                genericItem
                                    ? `app.invoices.detail.genericItemEdit.` + genericItemType
                                    : 'app.invoices.detail.genericItemNew.' + genericItemType
                            }
                        />
                    </PoolModalTitle>
                    <PoolModalContent>
                        <Grid container={true} columnSpacing={2} rowSpacing={0}>
                            <Grid item={true} xs={6}>
                                <PoolFormikTextfield
                                    label={<FormattedMessage id="app.label.title" />}
                                    fullWidth
                                    required={true}
                                    sx={{ mb: 2 }}
                                    name="title"
                                />
                            </Grid>
                            <Grid item={true} xs={6}>
                                <TaxRateAutocomplete
                                    disabled={readonly}
                                    taxRates={taxRates}
                                    name="taxRate"
                                    showLabel={false}
                                    textAlign="right"
                                    onValueChange={value => handleTaxRateSelected(formikProps, value)}
                                />
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={'description'}>
                                    {({ field, form }: FieldProps<string>) => (
                                        <PoolMarkdownEditor
                                            value={field.value ?? ''}
                                            onChange={description => form.setFieldValue('description', description)}
                                            readOnly={readonly}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </PoolModalContent>
                    <PoolModalActions>
                        <PoolButton variant="text" onClick={handleClose(formikProps)}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>
                        <PoolButton onClick={formikProps.submitForm}>
                            <FormattedMessage id={genericItem ? 'app.common.change' : 'app.common.add'} />
                        </PoolButton>
                    </PoolModalActions>
                </PoolModal>
            )}
        </Formik>
    );
};
